import React, { useEffect, useState } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Select,
  InputNumber,
  Switch,
  Upload,
  message,
  Typography,
  Divider,
  Row,
  Col,
  Image,
  Modal,
  Descriptions,
  Tag,
  Rate,
  Statistic,
  Alert,
} from 'antd'
import type { UploadProps, UploadFile } from 'antd'
import {
  PlusOutlined,
  SaveOutlined,
  EyeOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  UserOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { gardenService } from '@services/gardenService'
import { Garden } from '@types'
import { compressImage, uploadImage } from '@utils/compressImage'
import { apiService } from '@services/apiService'
import { Editor, Toolbar } from '@wangeditor/editor-for-react'
import '@wangeditor/editor/dist/css/style.css'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input
const { Option } = Select

/** 开关绑定 plan4Enabled/plan8Enabled；未碰开关时从 subscriptionPlans 嵌套读取，避免「全 undefined 却当作全开」 */
function resolvePlanEnabled(
  flat: boolean | undefined,
  nested?: { enabled?: boolean },
  fallback = true,
): boolean {
  if (typeof flat === 'boolean') return flat
  if (typeof nested?.enabled === 'boolean') return nested.enabled
  return fallback
}

function resolvePlanPrice(flat: unknown, nested: unknown, fallback: number): number {
  const n = Number(flat)
  if (!Number.isNaN(n) && n > 0 && flat !== '' && flat !== undefined && flat !== null) return n
  const nn = Number(nested)
  if (!Number.isNaN(nn) && nn > 0) return nn
  return fallback
}

/** 蔬菜图：表单里可能是 string，也可能被 Upload 写成 fileList */
function normalizeVegetableImageUrl(raw: unknown): string {
  if (raw == null) return ''
  if (typeof raw === 'string') {
    const s = raw.trim()
    // 过大 base64 不渲染（和微信小程序端保持一致）
    if (s.indexOf('data:') === 0 && s.length > 5000) return ''
    return s
  }
  if (Array.isArray(raw)) {
    const f = raw[0] as { url?: string; thumbUrl?: string } | undefined
    const u = (f?.url || f?.thumbUrl || '').trim()
    if (u.indexOf('data:') === 0 && u.length > 5000) return ''
    return u
  }
  if (typeof raw === 'object' && 'url' in (raw as object)) {
    const u = String((raw as { url?: string }).url || '').trim()
    if (u.indexOf('data:') === 0 && u.length > 5000) return ''
    return u
  }
  return ''
}

function formValuesToPreviewGarden(values: Record<string, any>): Garden {
  const cm = values.currentMenu || {}
  const sp = values.subscriptionPlans || {}
  const status = (values.status || 'pending') as Garden['status']

  const vegetablesRaw = Array.isArray(cm.vegetables) ? cm.vegetables : []
  const vegetables = vegetablesRaw.map((v: Record<string, unknown>) => ({
    ...v,
    name: v?.name,
    imageUrl: normalizeVegetableImageUrl(v?.imageUrl),
  }))

  const plan4On = resolvePlanEnabled(values.plan4Enabled, sp.monthly4, true)
  const plan8On = resolvePlanEnabled(values.plan8Enabled, sp.monthly8, true)

  return {
    id: 'preview',
    name: String(values.name || '').trim() || '（未填写菜园名称）',
    description: values.description,
    imageUrl: values.imageUrl,
    extraImages: values.extraImages || [],
    location: values.location,
    farmerId: values.farmerId ? String(values.farmerId).trim() : '—',
    farmerName: values.farmerName,
    phone: values.phone,
    features: Array.isArray(values.features) ? values.features : [],
    subscriptionCapacity: values.subscriptionCapacity,
    currentSubscriptions: 0,
    rating: 0,
    status,
    currentMenu: {
      name: cm.name || '',
      season: cm.season || '当季',
      description: cm.description || '',
      vegetables,
    },
    subscriptionPlans: {
      monthly4: {
        enabled: plan4On,
        userPrice: resolvePlanPrice(values.plan4UserPrice, sp.monthly4?.userPrice, 299),
        name: sp.monthly4?.name || '月付-配送4次/每周1次套餐',
        deliveryCount: Number(sp.monthly4?.deliveryCount) || 4,
        description: sp.monthly4?.description || '每月配送4次，每周1次，适合小家庭',
      },
      monthly8: {
        enabled: plan8On,
        userPrice: resolvePlanPrice(values.plan8UserPrice, sp.monthly8?.userPrice, 499),
        name: sp.monthly8?.name || '月付-配送8次/每周2次套餐',
        deliveryCount: Number(sp.monthly8?.deliveryCount) || 8,
        description: sp.monthly8?.description || '每月配送8次，每周2次，适合大家庭',
      },
    },
    isFeatured: !!values.isFeatured,
    isAd: !!values.isAd,
    adTitle: values.adTitle,
    adDescription: values.adDescription,
    adLink: values.adLink,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

function previewStatusTag(status: string) {
  const statusMap: Record<string, { color: string; text: string }> = {
    approved: { color: 'green', text: '已发布' },
    pending: { color: 'orange', text: '未发布' },
    inactive: { color: 'red', text: '已下架' },
  }
  const config = statusMap[status] || { color: 'default', text: status || '—' }
  return <Tag color={config.color}>{config.text}</Tag>
}

const GardenEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewGarden, setPreviewGarden] = useState<Garden | null>(null)

  // 图片上传状态
  const [gardenImageList, setGardenImageList] = useState<UploadFile[]>([])
  // 菜园主图 URL 映射：uid → 服务器 URL
  const [gardenImageUrlMap, setGardenImageUrlMap] = useState<{ [uid: string]: string }>({})
  const [menuImageList, setMenuImageList] = useState<UploadFile[]>([])
  const [menuImageUrl, setMenuImageUrl] = useState<string>('')
  const [vegetableImages, setVegetableImages] = useState<{ [key: number]: UploadFile[] }>({})
  const [vegetableImageUrlMap, setVegetableImageUrlMap] = useState<{ [key: number]: string }>({})
  // 富文本编辑器状态
  const [editor, setEditor] = useState<any>(null)
  const [detailContent, setDetailContent] = useState('')

  useEffect(() => {
    if (id) {
      setIsEdit(true)
      loadGardenData(id)
    } else {
      // 新增模式：开关字段与 subscriptionPlans 保持一致，避免预览/保存只读到嵌套、读不到 Switch
      form.setFieldsValue({
        status: 'pending',
        subscriptionCapacity: 100,
        isFeatured: false,
        isAd: false,
        plan4Enabled: true,
        plan8Enabled: true,
        plan4UserPrice: 299,
        plan8UserPrice: 499,
        currentMenu: {
          season: '当季',
          vegetables: [{ name: '', imageUrl: '' }],
        },
        subscriptionPlans: {
          monthly4: {
            enabled: true,
            userPrice: 299,
            name: '月付-配送4次/每周1次套餐',
            deliveryCount: 4,
            description: '每月配送4次，每周1次，适合小家庭',
          },
          monthly8: {
            enabled: true,
            userPrice: 499,
            name: '月付-配送8次/每周2次套餐',
            deliveryCount: 8,
            description: '每月配送8次，每周2次，适合大家庭',
          },
        },
      })
    }
  }, [id, form])

  const loadGardenData = async (gardenId: string) => {
    setLoading(true)
    try {
      const data = await gardenService.getGardenDetail(gardenId)
      form.setFieldsValue({
        ...data,
        plan4UserPrice: data.subscriptionPlans?.monthly4?.userPrice,
        plan8UserPrice: data.subscriptionPlans?.monthly8?.userPrice,
        plan4Enabled: data.subscriptionPlans?.monthly4?.enabled,
        plan8Enabled: data.subscriptionPlans?.monthly8?.enabled,
      })
      
      // 设置已有图片
      const existingFiles: UploadFile[] = []
      const urlMap: { [uid: string]: string } = {}
      if (data.imageUrl) {
        const uid = 'existing-main'
        existingFiles.push({ uid, name: 'garden-image-0.jpg', status: 'done', url: data.imageUrl })
        urlMap[uid] = data.imageUrl
      }
      if (Array.isArray(data.extraImages)) {
        data.extraImages.forEach((url: string, i: number) => {
          if (url) {
            const uid = 'existing-extra-' + i
            existingFiles.push({ uid, name: 'garden-image-' + (i + 1) + '.jpg', status: 'done', url })
            urlMap[uid] = url
          }
        })
      }
      setGardenImageList(existingFiles)
      setGardenImageUrlMap(urlMap)

      // 回显菜单图
      if (data.currentMenu?.imageUrl) {
        const menuUrl = normalizeVegetableImageUrl(data.currentMenu.imageUrl)
        setMenuImageList([{
          uid: 'existing-menu',
          name: 'menu-image.jpg',
          status: 'done',
          url: menuUrl || '',
        }])
        if (menuUrl) setMenuImageUrl(menuUrl)
      }

      // 回显蔬菜图
      const vegImages: { [key: number]: UploadFile[] } = {}
      const vegUrlMap: { [key: number]: string } = {}
      if (Array.isArray(data.currentMenu?.vegetables)) {
        data.currentMenu.vegetables.forEach((v: any, i: number) => {
          const imgUrl = typeof v.imageUrl === 'string' ? normalizeVegetableImageUrl(v.imageUrl) : ''
          if (imgUrl) {
            vegImages[i] = [{ uid: 'existing-veg-' + i, name: 'veg-' + i + '.jpg', status: 'done', url: imgUrl }]
            vegUrlMap[i] = imgUrl
          }
        })
      }
      setVegetableImages(vegImages)
      setVegetableImageUrlMap(vegUrlMap)

      // 回显富文本详情内容
      if (data.detailContent) {
        setDetailContent(data.detailContent)
      }
    } catch (error: any) {
      message.error('加载菜园数据失败')
      console.error('加载菜园数据错误:', error)
    } finally {
      setLoading(false)
    }
  }
  
  // 上传图片到服务器，返回 { url, dataUrl }
  const handleImageUpload = async (file: File): Promise<{ url: string; dataUrl: string }> => {
    return uploadImage(file, (formData) =>
      apiService.upload<{ url: string }>('/gardens/upload', formData),
      2
    )
  }

  // 从 UploadFile 列表同步 imageUrl + extraImages 到表单
  function syncGardenImageFields(fileList: UploadFile[], urlMap?: { [uid: string]: string }) {
    const map = urlMap || gardenImageUrlMap
    const urls = fileList
      .map(f => map[f.uid] || (f.url && !f.url.startsWith('data:') ? f.url : ''))
      .filter(Boolean)
    form.setFieldValue('imageUrl', urls[0] || '')
    form.setFieldValue('extraImages', urls.slice(1))
  }

  // 菜园主图上传配置（支持多张，第一张为主图，其余为额外图片）
  const gardenImageUploadProps: UploadProps = {
    listType: 'picture-card',
    fileList: gardenImageList,
    maxCount: 9,
    beforeUpload: async (file) => {
      if (!file.type.startsWith('image/')
        && !file.name.toLowerCase().endsWith('.heic')
        && !file.name.toLowerCase().endsWith('.heif')) {
        message.error('只能上传图片文件!')
        return Upload.LIST_IGNORE
      }
      if (file.size / 1024 / 1024 >= 30) {
        message.error('图片大小不能超过 30MB!')
        return Upload.LIST_IGNORE
      }
      try {
        const { url, dataUrl } = await handleImageUpload(file)
        const newFile: UploadFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: dataUrl,
          originFileObj: file,
        }
        const updatedList = [...gardenImageList, newFile]
        const updatedMap = { ...gardenImageUrlMap, [file.uid]: url }
        setGardenImageList(updatedList)
        setGardenImageUrlMap(updatedMap)
        syncGardenImageFields(updatedList, updatedMap)
        message.success('图片上传成功')
      } catch {
        message.error('图片上传失败')
      }
      return false
    },
    onRemove: (file) => {
      const filtered = gardenImageList.filter(f => f.uid !== file.uid)
      setGardenImageList(filtered)
      setGardenImageUrlMap(prev => {
        const next = { ...prev }
        delete next[file.uid]
        syncGardenImageFields(filtered, next)
        return next
      })
    },
  }

  // 菜单图片上传配置
  const menuImageUploadProps: UploadProps = {
    listType: 'picture-card',
    fileList: menuImageList,
    beforeUpload: async (file) => {
      if (!file.type.startsWith('image/')
        && !file.name.toLowerCase().endsWith('.heic')
        && !file.name.toLowerCase().endsWith('.heif')) {
        message.error('只能上传图片文件!')
        return Upload.LIST_IGNORE
      }
      if (file.size / 1024 / 1024 >= 30) {
        message.error('图片大小不能超过 30MB!')
        return Upload.LIST_IGNORE
      }
      try {
        const { url, dataUrl } = await handleImageUpload(file)
        const newFile: UploadFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: dataUrl,
          originFileObj: file,
        }
        setMenuImageList([newFile])
        setMenuImageUrl(url)
        form.setFieldValue(['currentMenu', 'imageUrl'], url)
        message.success('图片上传成功')
      } catch {
        message.error('图片上传失败')
      }
      return false
    },
    onRemove: () => {
      setMenuImageList([])
      setMenuImageUrl('')
      form.setFieldValue(['currentMenu', 'imageUrl'], '')
    },
    maxCount: 1,
  }

  // 蔬菜图片上传
  const getVegetableImageUploadProps = (fieldIndex: number): UploadProps => ({
    listType: 'picture-card',
    fileList: vegetableImages[fieldIndex] || [],
    beforeUpload: async (file) => {
      if (!file.type.startsWith('image/')
        && !file.name.toLowerCase().endsWith('.heic')
        && !file.name.toLowerCase().endsWith('.heif')) {
        message.error('只能上传图片文件!')
        return Upload.LIST_IGNORE
      }
      if (file.size / 1024 / 1024 >= 30) {
        message.error('图片大小不能超过 30MB!')
        return Upload.LIST_IGNORE
      }
      try {
        const { url, dataUrl } = await handleImageUpload(file)
        const newFile: UploadFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: dataUrl,
          originFileObj: file,
        }
        setVegetableImages(prev => ({
          ...prev,
          [fieldIndex]: [newFile]
        }))
        setVegetableImageUrlMap(prev => ({ ...prev, [fieldIndex]: url }))
        const vegetables = form.getFieldValue(['currentMenu', 'vegetables']) || []
        vegetables[fieldIndex] = {
          ...vegetables[fieldIndex],
          imageUrl: url
        }
        form.setFieldValue(['currentMenu', 'vegetables'], vegetables)
        message.success('图片上传成功')
      } catch {
        message.error('图片上传失败')
      }
      return false
    },
    onRemove: () => {
      setVegetableImages(prev => ({
        ...prev,
        [fieldIndex]: []
      }))
      setVegetableImageUrlMap(prev => {
        const next = { ...prev }
        delete next[fieldIndex]
        return next
      })
      const vegetables = form.getFieldValue(['currentMenu', 'vegetables']) || []
      vegetables[fieldIndex] = {
        ...vegetables[fieldIndex],
        imageUrl: ''
      }
      form.setFieldValue(['currentMenu', 'vegetables'], vegetables)
    },
    maxCount: 1,
  })

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true)
    try {
      // 从 gardenImageUrlMap 取真实 URL，避免 Form+Upload 将 imageUrl 写成 Upload 对象
      let imageUrl = typeof values.imageUrl === 'string' && !values.imageUrl.startsWith('data:')
        ? values.imageUrl
        : ''
      let extraImages: string[] = Array.isArray(values.extraImages) ? values.extraImages : []
      if (gardenImageList.length > 0) {
        const allUrls = gardenImageList
          .map(f => gardenImageUrlMap[f.uid] || (f.url && !f.url.startsWith('data:') ? f.url : ''))
          .filter(Boolean)
        imageUrl = allUrls[0] || ''
        extraImages = allUrls.slice(1)
      }

      // 构建菜园数据
      const gardenData: Partial<Garden> = {
        name: values.name,
        description: values.description,
        imageUrl,
        extraImages,
        location: values.location,
        farmerName: values.farmerName,
        phone: values.phone,
        features: values.features || [],
        subscriptionCapacity: values.subscriptionCapacity,
        isFeatured: values.isFeatured || false,
        isAd: values.isAd || false,
        adTitle: values.adTitle,
        adDescription: values.adDescription,
        adLink: values.adLink,
        detailContent: detailContent,
        currentMenu: {
          name: values.currentMenu?.name || '',
          season: values.currentMenu?.season || '当季',
          description: values.currentMenu?.description || '',
          vegetables: (values.currentMenu?.vegetables || []).map((v: any, i: number) => ({
            ...v,
            imageUrl: vegetableImageUrlMap[i] || (typeof v.imageUrl === 'string' ? normalizeVegetableImageUrl(v.imageUrl) : ''),
          })),
        },
        subscriptionPlans: {
          monthly4: {
            enabled: resolvePlanEnabled(values.plan4Enabled, values.subscriptionPlans?.monthly4, true),
            userPrice: resolvePlanPrice(values.plan4UserPrice, values.subscriptionPlans?.monthly4?.userPrice, 299),
            name: '月付-配送4次/每周1次套餐',
            deliveryCount: 4,
            description: '每月配送4次，每周1次，适合小家庭',
          },
          monthly8: {
            enabled: resolvePlanEnabled(values.plan8Enabled, values.subscriptionPlans?.monthly8, true),
            userPrice: resolvePlanPrice(values.plan8UserPrice, values.subscriptionPlans?.monthly8?.userPrice, 499),
            name: '月付-配送8次/每周2次套餐',
            deliveryCount: 8,
            description: '每月配送8次，每周2次，适合大家庭',
          },
        },
      }

      if (isEdit && id) {
        // 后端：内容变更会将菜园置为未发布，需在菜园管理中重新发布
        await gardenService.updateGarden(id, gardenData)
        message.success('菜园更新成功')
        message.info('内容已保存为「未发布」。请在「菜园管理」中重新发布后，用户端才会看到最新内容。', 5)
      } else {
        // 新建：固定未发布，由菜园管理列表负责发布/下架
        await gardenService.createGarden({ ...gardenData, status: 'pending' })
        message.success('菜园创建成功')
        message.info('已保存为「未发布」。请到「菜园管理」中发布后，用户端才可浏览。', 5)
      }

      // 返回列表页
      navigate('/gardens')
    } catch (error: any) {
      message.error(error || '保存失败')
      console.error('保存菜园错误:', error)
    } finally {
      setSubmitLoading(false)
    }
  }

  const handlePreview = () => {
    const values = form.getFieldsValue(true)
    // 与 Upload 状态对齐：避免 imageUrl 未写回表单导致预览裂图
    if (gardenImageList.length > 0) {
      const allUrls = gardenImageList
        .map(f => gardenImageUrlMap[f.uid] || (f.url && !f.url.startsWith('data:') ? f.url : ''))
        .filter(Boolean)
      values.imageUrl = allUrls[0] || ''
      values.extraImages = allUrls.slice(1)
    }
    if (menuImageUrl) {
      values.currentMenu = { ...values.currentMenu, imageUrl: menuImageUrl }
    } else {
      const menuCover = menuImageList[0]?.url
      if (menuCover && !menuCover.startsWith('data:')) {
        values.currentMenu = { ...values.currentMenu, imageUrl: menuCover }
      }
    }
    const vegList = [...(values.currentMenu?.vegetables || [])]
    Object.entries(vegetableImageUrlMap).forEach(([idx, url]) => {
      const i = Number(idx)
      if (url && vegList[i]) {
        vegList[i] = { ...vegList[i], imageUrl: url }
      }
    })
    values.currentMenu = { ...values.currentMenu, vegetables: vegList }
    // 详情富文本内容
    values.detailContent = detailContent
    // 显式读取套餐开关（部分情况下 getFieldsValue 与 Switch 不同步）
    values.plan4Enabled = form.getFieldValue('plan4Enabled')
    values.plan8Enabled = form.getFieldValue('plan8Enabled')
    setPreviewGarden(formValuesToPreviewGarden(values))
    setPreviewOpen(true)
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          {isEdit ? '编辑菜园' : '添加菜园'}
        </Title>
        <Text type="secondary">
          {isEdit ? '编辑菜园的详细信息' : '填写菜园的详细信息，创建新的菜园'}
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Card title="基本信息" style={{ marginBottom: '24px' }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="菜园名称"
                name="name"
                rules={[{ required: true, message: '请输入菜园名称' }]}
              >
                <Input placeholder="请输入菜园名称" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="菜园地址"
                name="location"
                rules={[{ required: true, message: '请输入菜园地址' }]}
              >
                <Input placeholder="省市区或详细地址" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="菜园描述"
            name="description"
            rules={[{ required: true, message: '请输入菜园描述' }]}
          >
            <TextArea rows={4} placeholder="请输入菜园描述" />
          </Form.Item>

          <Form.Item label="菜园图片" name="imageUrl">
            <Upload {...gardenImageUploadProps}>
              {gardenImageList.length < 9 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              )}
            </Upload>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              可上传多张（最多9张），第一张为主图。支持 JPG、PNG，单张不超过 5MB
            </Text>
          </Form.Item>

          <Form.Item label="菜园详情（富文本编辑器）" style={{ marginBottom: 16 }}>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #d9d9d9', background: '#fafafa' }}>
                <Toolbar
                  editor={editor}
                  defaultConfig={{
                    toolbarKeys: [
                      'headerSelect',
                      '|',
                      'bold', 'italic', 'underline',
                      '|',
                      'color', 'bgColor',
                      '|',
                      'fontSize', 'lineHeight',
                      '|',
                      'bulletedList', 'numberedList',
                      '|',
                      'justifyLeft', 'justifyCenter', 'justifyRight',
                      '|',
                      'undo', 'redo',
                      '|',
                      'clearStyle',
                    ],
                  }}
                  mode="default"
                  style={{ border: 'none', flex: 1 }}
                />
                <Upload
                  accept="image/*,.heic,.heif"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    if (!file.type.startsWith('image/')
                      && !file.name.toLowerCase().endsWith('.heic')
                      && !file.name.toLowerCase().endsWith('.heif')) {
                      message.error('只能上传图片文件!')
                      return Upload.LIST_IGNORE
                    }
                    try {
                      const { url } = await handleImageUpload(file)
                      if (editor) {
                        editor.dangerouslyInsertHtml('<img src="' + url + '" style="max-width:100%;" />')
                      } else {
                        setDetailContent(prev => prev + '<img src="' + url + '" style="max-width:100%;" />')
                      }
                      message.success('图片已插入')
                    } catch {
                      message.error('图片上传失败')
                    }
                    return false
                  }}
                >
                  <Button type="primary" size="small" icon={<PlusOutlined />}
                    style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>
                    插入图片
                  </Button>
                </Upload>
              </div>
              <Editor
                defaultConfig={{
                  placeholder: '请输入菜园详情内容（支持文字排版、图片插入，参考淘宝详情页）...',
                  MENU_CONF: {
                    uploadImage: {
                      async customUpload(file: File, insertFn: (url: string) => void) {
                        try {
                          const { url } = await handleImageUpload(file)
                          insertFn(url)
                          message.success('图片上传成功')
                        } catch {
                          message.error('图片上传失败')
                        }
                      },
                    },
                  },
                }}
                value={detailContent}
                onCreated={setEditor}
                onChange={(ed: any) => setDetailContent(ed.getHtml())}
                mode="default"
                style={{ minHeight: 300, maxHeight: 600, overflowY: 'auto' }}
              />
            </div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
              点击工具栏图片图标或右侧「插入图片」按钮上传图片。支持文字排版，用户在小程序端点击菜园介绍卡片后查看此内容。
            </Text>
          </Form.Item>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                label="农户姓名"
                name="farmerName"
                rules={[{ required: true, message: '请输入农户姓名' }]}
              >
                <Input placeholder="用于管理端展示，无需单独维护农户档案" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="联系电话"
                name="phone"
                rules={[{ required: true, message: '请输入联系电话' }]}
              >
                <Input placeholder="农户联系电话" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="订阅承载量" name="subscriptionCapacity">
            <InputNumber min={1} max={1000} style={{ width: '200px' }} />
          </Form.Item>

          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            保存后菜园为「未发布」，会出现在「菜园管理」列表中。发布、下架请在菜园管理页通过列表操作或状态筛选相关功能处理，不在此表单修改状态。
          </Text>
        </Card>

        <Card title="订阅套餐" style={{ marginBottom: '24px' }}>
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Card type="inner" title="月付-配送4次/每周1次套餐">
                <Form.Item label="启用" name="plan4Enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="用户价格" name="plan4UserPrice">
                  <InputNumber min={0} prefix="¥" style={{ width: '100%' }} />
                </Form.Item>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card type="inner" title="月付-配送8次/每周2次套餐">
                <Form.Item label="启用" name="plan8Enabled" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item label="用户价格" name="plan8UserPrice">
                  <InputNumber min={0} prefix="¥" style={{ width: '100%' }} />
                </Form.Item>
              </Card>
            </Col>
          </Row>
        </Card>

        <Card title="营销设置" style={{ marginBottom: '24px' }}>
          <Form.Item label="精选菜园" name="isFeatured" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Divider />

          <Form.Item label="广告菜园" name="isAd" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isAd !== currentValues.isAd}
          >
            {({ getFieldValue }) =>
              getFieldValue('isAd') ? (
                <>
                  <Form.Item label="广告标题" name="adTitle">
                    <Input placeholder="请输入广告标题" />
                  </Form.Item>
                  <Form.Item label="广告描述" name="adDescription">
                    <TextArea rows={2} placeholder="请输入广告描述" />
                  </Form.Item>
                  <Form.Item label="广告链接" name="adLink">
                    <Input placeholder="请输入广告链接" />
                  </Form.Item>
                </>
              ) : null
            }
          </Form.Item>
        </Card>

        <Card>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={submitLoading}>
              保存
            </Button>
            <Button icon={<EyeOutlined />} onClick={handlePreview}>
              预览
            </Button>
            <Button onClick={() => navigate('/gardens')}>取消</Button>
          </Space>
        </Card>
      </Form>

      <Modal
        title="菜园预览（用户端效果示意）"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={
          <Button type="primary" onClick={() => setPreviewOpen(false)}>
            关闭
          </Button>
        }
        width={960}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingTop: 8 } }}
        destroyOnClose
      >
        {previewGarden && (
          <>
            <Alert
              type="info"
              showIcon
              message="根据当前表单即时生成，未保存的内容不会出现在用户小程序/前台。"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16}>
                <Card
                  cover={
                    <>
                      <Image
                        alt={previewGarden.name}
                        src={previewGarden.imageUrl}
                        fallback="https://placehold.co/600x400/EEEEEE/999999?text=菜园图片"
                        style={{ maxHeight: 360, objectFit: 'cover' }}
                      />
                      {previewGarden.extraImages && previewGarden.extraImages.length > 0 && (
                        <Row gutter={[8, 8]} style={{ padding: '12px 12px 0' }}>
                          {previewGarden.extraImages.map((url: string, i: number) => (
                            <Col key={i} xs={8} sm={6}>
                              <Image
                                src={url}
                                alt={'额外图片 ' + (i + 1)}
                                fallback="https://placehold.co/200x150/EEEEEE/999999?text=图片"
                                style={{ height: 100, objectFit: 'cover', borderRadius: 4 }}
                              />
                            </Col>
                          ))}
                        </Row>
                      )}
                    </>
                  }
                >
                  <div style={{ marginBottom: 12 }}>
                    <Title level={3} style={{ marginBottom: 8 }}>
                      {previewGarden.name}
                      {previewGarden.isFeatured && (
                        <Tag color="gold" style={{ marginLeft: 8 }}>
                          精选
                        </Tag>
                      )}
                      {previewGarden.isAd && (
                        <Tag color="red" style={{ marginLeft: 8 }}>
                          广告
                        </Tag>
                      )}
                    </Title>
                    <Space wrap>
                      {previewStatusTag(previewGarden.status)}
                      <Rate disabled value={previewGarden.rating || 0} style={{ fontSize: 14 }} />
                      <Text type="secondary">新菜园预览无评分</Text>
                    </Space>
                  </div>
                  <Paragraph>
                    {previewGarden.description?.trim() || (
                      <Text type="secondary">（未填写描述）</Text>
                    )}
                  </Paragraph>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="菜园地址">
                      <EnvironmentOutlined style={{ marginRight: 8 }} />
                      {previewGarden.location?.trim() || (
                        <Text type="secondary">未填写</Text>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="农户">
                      <Space>
                        <UserOutlined />
                        {previewGarden.farmerName?.trim() || (
                          <Text type="secondary">未填写</Text>
                        )}
                        <PhoneOutlined />
                        {previewGarden.phone?.trim() || (
                          <Text type="secondary">未填写</Text>
                        )}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="订阅容量">
                      0 / {previewGarden.subscriptionCapacity ?? '—'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {(previewGarden.currentMenu?.name ||
                  previewGarden.currentMenu?.description ||
                  (previewGarden.currentMenu?.vegetables?.length ?? 0) > 0) && (
                  <Card title="当前菜单" style={{ marginTop: 16 }}>
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="菜单名称">
                        {previewGarden.currentMenu?.name || (
                          <Text type="secondary">未填写</Text>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="季节">
                        {previewGarden.currentMenu?.season}
                      </Descriptions.Item>
                      <Descriptions.Item label="描述" span={2}>
                        {previewGarden.currentMenu?.description || (
                          <Text type="secondary">未填写</Text>
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                    {previewGarden.currentMenu?.vegetables &&
                      previewGarden.currentMenu.vegetables.length > 0 && (
                        <>
                          <Title level={5} style={{ marginTop: 12 }}>
                            蔬菜列表
                          </Title>
                          <Row gutter={[12, 12]}>
                            {previewGarden.currentMenu.vegetables.map((veg, index) => (
                              <Col key={index} xs={12} sm={8} md={6}>
                                <Card
                                  size="small"
                                  cover={
                                    <Image
                                      src={veg.imageUrl}
                                      alt={veg.name}
                                      fallback="https://placehold.co/100x100/EEEEEE/999999?text=蔬菜"
                                    />
                                  }
                                >
                                  <Card.Meta
                                    description={veg.name?.trim() || '未命名'}
                                  />
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </>
                      )}
                  </Card>
                )}

                {previewGarden.isAd && (
                  <Card title="广告信息" style={{ marginTop: 16 }}>
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="标题">
                        {previewGarden.adTitle || <Text type="secondary">未填写</Text>}
                      </Descriptions.Item>
                      <Descriptions.Item label="描述">
                        {previewGarden.adDescription || <Text type="secondary">未填写</Text>}
                      </Descriptions.Item>
                      <Descriptions.Item label="链接">
                        {previewGarden.adLink || <Text type="secondary">未填写</Text>}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                )}
              </Col>

              <Col xs={24} lg={8}>
                <Card title="统计（示意）" size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={[8, 8]}>
                    <Col span={12}>
                      <Statistic title="总营收" value={0} prefix="¥" />
                    </Col>
                    <Col span={12}>
                      <Statistic title="总订单" value={0} suffix="单" />
                    </Col>
                  </Row>
                </Card>
                <Card title="订阅套餐" size="small">
                  {previewGarden.subscriptionPlans?.monthly4?.enabled && (
                    <Card
                      type="inner"
                      size="small"
                      title={previewGarden.subscriptionPlans.monthly4.name || '月付-配送4次/每周1次套餐'}
                      style={{ marginBottom: 12 }}
                      extra={<Tag color="green">启用</Tag>}
                    >
                      <Text>
                        ¥{previewGarden.subscriptionPlans.monthly4.userPrice} · 每月
                        {previewGarden.subscriptionPlans.monthly4.deliveryCount}次
                      </Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {previewGarden.subscriptionPlans.monthly4.description}
                        </Text>
                      </div>
                    </Card>
                  )}
                  {previewGarden.subscriptionPlans?.monthly8?.enabled && (
                    <Card
                      type="inner"
                      size="small"
                      title={previewGarden.subscriptionPlans.monthly8.name || '月付-配送8次/每周2次套餐'}
                      extra={<Tag color="green">启用</Tag>}
                    >
                      <Text>
                        ¥{previewGarden.subscriptionPlans.monthly8.userPrice} · 每月
                        {previewGarden.subscriptionPlans.monthly8.deliveryCount}次
                      </Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {previewGarden.subscriptionPlans.monthly8.description}
                        </Text>
                      </div>
                    </Card>
                  )}
                  {!previewGarden.subscriptionPlans?.monthly4?.enabled &&
                    !previewGarden.subscriptionPlans?.monthly8?.enabled && (
                      <Text type="secondary">未启用任何订阅套餐（与用户端一致时不展示套餐）</Text>
                    )}
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Modal>
    </div>
  )
}

export default GardenEdit

